import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import Button from './ui/Button'
import { navLinks } from '../lib/constants'
import { LIQUIMASTER_APP_URL } from '../lib/config'

export default function Navbar() {
  const { t } = useTranslation()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const rafRef = useRef(0)

  const onScroll = useCallback(() => {
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => {
      setScrolled(window.scrollY > 20)
      rafRef.current = 0
    })
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(rafRef.current)
    }
  }, [onScroll])

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          scrolled
            ? 'mx-auto mt-3 max-w-5xl rounded-full bg-white/90 backdrop-blur-xl shadow-[0_4px_24px_rgba(21,32,56,0.08)] border border-navy-100/60 px-5'
            : 'mx-auto mt-0 max-w-6xl rounded-none bg-transparent px-4 sm:px-6 lg:px-8'
        }`}
      >
        <div className="flex items-center justify-between h-14 md:h-14">
          {/* Logo */}
          <Link 
            to="/" 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2.5 group"
          >
            <img
              src={scrolled ? '/images/logoupgradedwithoutbg.png' : '/images/logo.png'}
              alt="FindYourRisk Logo"
              className={`h-10 w-auto object-contain transition-all ${
                scrolled ? 'rounded-none shadow-none' : 'rounded-lg shadow-sm'
              }`}
            />
            <span className={`text-lg font-bold font-heading tracking-tight group-hover:text-orange-500 transition-colors ${
              scrolled ? 'text-navy-900' : 'text-white'
            }`}>
              FindYourRisk
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-7">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className={`text-sm font-medium hover:text-orange-500 transition-colors relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-orange-500 after:transition-all hover:after:w-full ${
                  scrolled ? 'text-navy-600' : 'text-navy-200'
                }`}
              >
                {t(`nav.${link.key}`)}
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            <Button href={LIQUIMASTER_APP_URL} size="sm" variant={scrolled ? 'secondary' : 'primary'}>
              {t('nav.getStarted')}
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${scrolled ? 'text-navy-700 hover:bg-navy-50' : 'text-white hover:bg-white/10'}`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-t border-neutral-100 shadow-lg overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block text-navy-600 font-medium py-2.5 hover:text-orange-500 transition-colors text-sm"
                >
                  {t(`nav.${link.key}`)}
                </a>
              ))}
              <div className="pt-3 border-t border-neutral-100 flex items-center justify-between">
                <Button href={LIQUIMASTER_APP_URL} size="sm" variant="secondary">
                  {t('nav.getStarted')}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
